import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { UserProfile } from '../../../../../../corelogic/domain/models/user.model';
import {
  detectMaliciousContent,
  ImageValidationResult,
  validateImageFile,
} from '../../../../../../corelogic/domain/utils/fileUploadSecurity.utils';
import { store } from '../../../../../../corelogic/state/store';
import { updateUserProfile } from '../../../../../../corelogic/state/user/user.action';
import { UPDATE_USER_PROFILE_SUCCESS } from '../../../../../../corelogic/state/user/user.action.type';
import { SuccessModal } from '../../Shared/SuccessModal/SuccessModal';

interface ProfilePictureModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  currentPicture: string;
  userProfile: UserProfile | null;
}

export const ProfilePictureModal: React.FC<ProfilePictureModalProps> = ({
  isOpen,
  onClose,
  token,
  currentPicture,
  userProfile,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] =
    useState<ImageValidationResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pendingProfilePictureUpdate, setPendingProfilePictureUpdate] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedImage(null);
    setError('');
    setValidationResult(null);
  };

  const handleSecureFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setError('');
    setIsValidating(true);

    try {
      const result = await validateImageFile(event);

      if (!result.isValid) {
        setError(result.error || 'File validation failed');
        setValidationResult(null);
        setSelectedImage(null);
        return;
      }

      // Additional malicious content check
      if (result.sanitizedFile) {
        const isMalicious = await detectMaliciousContent(result.sanitizedFile);
        if (isMalicious) {
          setError(
            'File contains potentially dangerous content and cannot be uploaded.',
          );
          setValidationResult(null);
          setSelectedImage(null);
          return;
        }
      }

      // Use compressed version for display and upload
      if (result.compressedDataUrl) {
        setSelectedImage(result.compressedDataUrl);
        setValidationResult(result);
      }
    } catch (error) {
      setError('An error occurred while processing the file.');
      console.error('File validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSecureCapture = async () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      const context = canvas.getContext('2d');
      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob and validate
        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              setError('Failed to capture image');
              return;
            }

            const file = new File([blob], 'camera-capture.jpg', {
              type: 'image/jpeg',
            });

            setIsValidating(true);
            try {
              const result = await validateImageFile(file);

              if (!result.isValid) {
                setError(result.error || 'Captured image validation failed');
                return;
              }

              if (result.compressedDataUrl) {
                setSelectedImage(result.compressedDataUrl);
                setValidationResult(result);
              }
            } catch (error) {
              setError('Failed to process captured image');
              console.error('Capture validation error:', error);
            } finally {
              setIsValidating(false);
            }
          },
          'image/jpeg',
          0.8,
        );
      }
    }
  };

  const startCamera = async () => {
    try {
      if (videoRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 800, height: 600 },
        });
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();

      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const handleSecureSubmit = async () => {
    if (!selectedImage || !validationResult?.isValid) {
      setError('Please select or capture a valid image');
      return;
    }

    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }

    if (!userProfile) {
      setError('User profile not available. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setPendingProfilePictureUpdate(true);
    setError('');

    try {
      // For now, we're storing as base64 - in production, you'd upload to S3 or similar
      // The compressed image is already validated and safe to use

      /*
      // Future implementation for secure upload to S3:
      const formData = new FormData();
      if (validationResult.sanitizedFile) {
        formData.append('profilePicture', validationResult.sanitizedFile);
        
        await store.dispatch(
          updateUserProfile({
            token,
            currentProfile: userProfile,
            newData: { profilePicture: formData },
            retry: 0
          })
        );
      }
      */

      // Current implementation - store compressed base64
      // This is already validated and safe
      await store.dispatch(
        updateUserProfile({
          token,
          currentProfile: userProfile,
          newData: { profilePicture: selectedImage },
          retry: 0,
        }),
      );
    } catch (error) {
      setError('Failed to update profile picture. Please try again.');
      console.error('Profile picture update error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    setValidationResult(null);
    setError('');

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Start camera when modal is open and camera tab is selected
  React.useEffect(() => {
    if (isOpen && activeTab === 1) {
      startCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, activeTab]);

  // Listen for successful profile picture update
  useEffect(() => {
    const subscription = store.action$.subscribe((action) => {
      if (
        action.type === UPDATE_USER_PROFILE_SUCCESS &&
        pendingProfilePictureUpdate
      ) {
        setShowSuccessModal(true);
        onClose();
        setPendingProfilePictureUpdate(false);
        setSelectedImage(null);
        setValidationResult(null);
        setError('');
      }
    });

    return () => subscription.unsubscribe();
  }, [pendingProfilePictureUpdate, onClose]);

  // Clean up on modal close
  const handleClose = () => {
    if (!isSubmitting) {
      stopCamera();
      setSelectedImage(null);
      setValidationResult(null);
      setError('');
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: '450px',
            width: '100%',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Rubik Mono One',
            fontSize: '1.1rem',
            textAlign: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pb: 1,
          }}
        >
          Profile Picture
        </DialogTitle>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .Mui-selected': {
              color: '#cc0000',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#cc0000',
            },
          }}
        >
          <Tab icon={<PhotoLibraryIcon />} label="UPLOAD" />
          <Tab icon={<PhotoCameraIcon />} label="CAMERA" />
        </Tabs>

        <DialogContent sx={{ p: 2 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}
            >
              {error}
            </Alert>
          )}

          {isValidating && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CircularProgress size={20} sx={{ color: '#cc0000', mr: 1 }} />
              <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                Validating file security...
              </Typography>
            </Box>
          )}

          {activeTab === 0 && (
            <Box sx={{ textAlign: 'center' }}>
              {selectedImage ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={selectedImage}
                    alt="Selected profile"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '300px',
                      borderRadius: '4px',
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(139,0,0,0.8)',
                      },
                    }}
                    onClick={handleClearImage}
                  >
                    <DeleteIcon />
                  </IconButton>

                  {validationResult && (
                    <Box sx={{ mt: 1, textAlign: 'left' }}>
                      <Typography
                        variant="caption"
                        color="rgba(255, 255, 255, 0.7)"
                      >
                        ✓ File validated successfully
                        {validationResult.dimensions &&
                          ` • ${validationResult.dimensions.width}x${validationResult.dimensions.height}px`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      border: '2px dashed rgba(255,255,255,0.3)',
                      borderRadius: '8px',
                      p: 4,
                      mb: 2,
                      cursor: isValidating ? 'not-allowed' : 'pointer',
                      opacity: isValidating ? 0.6 : 1,
                      '&:hover': !isValidating
                        ? {
                            borderColor: 'rgba(255,255,255,0.5)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }
                        : {},
                    }}
                    onClick={() =>
                      !isValidating && fileInputRef.current?.click()
                    }
                  >
                    <PhotoLibraryIcon
                      sx={{
                        fontSize: 60,
                        color: 'rgba(255,255,255,0.5)',
                        mb: 2,
                      }}
                    />
                    <Typography>
                      Click to select an image from your device
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}
                    >
                      JPG, PNG or WebP (max 2MB, 2000x2000px)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.4)',
                        display: 'block',
                        mt: 1,
                      }}
                    >
                      Files are automatically validated for security
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/jpeg, image/png, image/webp"
                    onChange={handleSecureFileSelect}
                    disabled={isValidating}
                  />
                </>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ textAlign: 'center' }}>
              {selectedImage ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={selectedImage}
                    alt="Captured profile"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '300px',
                      borderRadius: '4px',
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(139,0,0,0.8)',
                      },
                    }}
                    onClick={handleClearImage}
                  >
                    <DeleteIcon />
                  </IconButton>

                  {validationResult && validationResult.dimensions && (
                    <Box sx={{ mt: 1, textAlign: 'left' }}>
                      <Typography
                        variant="caption"
                        color="rgba(255, 255, 255, 0.7)"
                      >
                        ✓ Captured image validated •{' '}
                        {validationResult.dimensions.width}x
                        {validationResult.dimensions.height}px
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <>
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      style={{
                        width: '100%',
                        maxHeight: '300px',
                        backgroundColor: '#000',
                        borderRadius: '4px',
                      }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<PhotoCameraIcon />}
                      onClick={handleSecureCapture}
                      disabled={isValidating}
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#8B0000',
                        '&:hover': {
                          backgroundColor: '#aa0000',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(139, 0, 0, 0.5)',
                        },
                      }}
                    >
                      {isValidating ? 'Processing...' : 'Capture'}
                    </Button>
                  </Box>
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            sx={{ color: 'white' }}
            disabled={isSubmitting || isValidating}
          >
            Cancel
          </Button>
          <Tooltip title="Coming Soon!" arrow>
            <span>
              <Button
                onClick={handleSecureSubmit}
                variant="contained"
                disabled={true}
                sx={{
                  backgroundColor: 'rgba(139, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 0, 0, 0.5)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(139, 0, 0, 0.5)',
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Save'
                )}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Profile Picture Updated"
        message="Your profile picture has been successfully updated and validated."
        autoHideDuration={3000}
      />
    </>
  );
};
